import React, { useMemo, useState } from 'react';
import { ClockIcon, PencilIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { useMemoryBankNotesBatch } from '@/api/core/ai/memory-bank/queries';
import type {
  MemoryBankNote,
  NoteUsage,
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import { Skeleton } from '../ui/skeleton';
import { NoteDisplay } from './NoteDisplay';
import { cn } from '@/lib/utils';
import { Badge } from '../ui/badge';
import { formatDatetime } from '@/utils/dateUtils';
import { Button } from '../ui/button';
import { useNoteActor } from '@/hooks/useNoteActor';
import {
  WORKFLOW_STEP_LABELS,
  WORKFLOW_STEP_COLORS,
} from '@/hooks/useWorkflowStepFilter';

interface NotesUsedDisplayProps {
  notesUsed: NoteUsage[];
  className?: string;
  onEditNote: (note: MemoryBankNote) => void;
  metadata?: Partial<MemoryBankNoteMetadata>;
  entityType?: MemoryBankSupportedEntityTypes;
  entityId?: string;
}

function NoteUsedItem({
  note,
  usage,
  onEdit,
}: {
  note?: MemoryBankNote;
  usage: NoteUsage;
  onEdit: (note: MemoryBankNote) => void;
}) {
  const gt = useGT();
  const [isExpanded, setIsExpanded] = useState(false);
  const versionUsed = usage.version;
  const actor = useNoteActor(versionUsed, note);
  const isCreationUsage = versionUsed === 0;

  return (
    <div key={`${usage.noteId}`} className="border rounded-lg p-3 relative">
      <div className="flex items-start justify-between gap-2 mb-2 pr-8">
        <div className="flex items-center gap-1 text-xs text-gray-500 flex-wrap">
          <div className="flex items-center gap-1">
            <ClockIcon className="h-3 w-3" />
            {formatDatetime(usage.createdAt)}
          </div>
          <span>
            <Badge
              variant="secondary"
              className={cn(
                isCreationUsage && 'bg-green-200 text-green-700',
                !isCreationUsage && 'bg-blue-200 text-blue-700',
              )}
            >
              {isCreationUsage ? (
                <T context="badge label indicating a note was created at this usage event">
                  Created
                </T>
              ) : (
                <T context="badge label indicating a note was used at this usage event">
                  Used
                </T>
              )}
            </Badge>
          </span>
          {isCreationUsage ? (
            <span>{actor}</span>
          ) : (
            <>
              <T context="preposition connecting usage badge to workflow step badge, as in 'Used by [step]'">
                by
              </T>
              <Badge
                variant="secondary"
                className={cn(
                  usage.workflowStep &&
                    WORKFLOW_STEP_COLORS[usage.workflowStep],
                )}
              >
                {usage.workflowStep && WORKFLOW_STEP_LABELS[usage.workflowStep]}
              </Badge>
            </>
          )}
        </div>
      </div>
      <div
        className={cn('cursor-pointer transition-all duration-200')}
        onClick={() => setIsExpanded(!isExpanded)}
        title={gt('Click to expand', {
          context: 'tooltip for expandable note content',
        })}
      >
        <NoteDisplay
          content={note?.note || ''}
          showFullContent={isExpanded}
          maxHeight={isExpanded ? undefined : 'max-h-20'}
        />
      </div>

      <div className="absolute top-2 right-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          onClick={(e) => {
            e.stopPropagation();
            if (!note) return;

            onEdit(note);
          }}
          disabled={!note}
        >
          <PencilIcon className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export function NotesUsedDisplay({
  notesUsed,
  onEditNote,
  className = '',
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  metadata: _metadata,
  entityType: _entityType,
  entityId: _entityId,
}: NotesUsedDisplayProps) {
  // Extract unique note IDs for batch fetching
  const noteIds = useMemo(
    () => [...new Set(notesUsed.map((usage) => usage.noteId))],
    [notesUsed],
  );

  const { data: notes, isLoading } = useMemoryBankNotesBatch(noteIds);

  const notesWithUsages = useMemo(() => {
    return notesUsed
      ?.map((noteUsage) => ({
        usage: noteUsage,
        note: notes?.find((note) => note.id === noteUsage.noteId),
      }))
      .filter(({ usage }) => usage !== undefined);
  }, [notes, notesUsed]);

  if (isLoading) {
    return (
      <div className={className}>
        <Skeleton className="h-4 bg-gray-200 rounded mb-2" />
        <Skeleton className="h-4 bg-gray-200 rounded w-3/4" />
      </div>
    );
  }

  if (
    !notesWithUsages ||
    notesWithUsages.length === 0 ||
    notesUsed.length === 0
  ) {
    return null;
  }

  return (
    <div className={className}>
      {/* Notes list */}
      <div className="space-y-3">
        {notesWithUsages?.map(({ note, usage }) => (
          <NoteUsedItem
            key={usage.noteId}
            note={note}
            usage={usage}
            onEdit={onEditNote}
          />
        ))}
      </div>
    </div>
  );
}
